import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api, formatDate } from '../../lib/api';
import { esMovil } from '../../lib/utils';
import { SectionCard } from '../Common';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Camera, Upload, Trash2, Loader2, X, Images, Check, Download, RefreshCcw } from 'lucide-react';
import FotoZoom from '../fotos/FotoZoom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const GRUPOS = [
  { tipo: 'antes', label: 'Antes' },
  { tipo: 'despues', label: 'Después' },
  { tipo: 'comparativa', label: 'Comparativa' },
  { tipo: 'otra', label: 'Otras' },
];

// Marcadores sobre la foto (rojo/amarillo + nota), igual que el mapa facial.
const MK_COLORS = { rojo: '#e11d48', amarillo: '#f59e0b' };
const mkUid = () => (window.crypto?.randomUUID
  ? window.crypto.randomUUID()
  : `m${Date.now()}${Math.random().toString(36).slice(2, 7)}`);

/** Foto a pantalla completa con marcadores: clic para poner un punto, nota por
 *  punto, se guardan en la propia foto (mismo sistema que el mapa facial).
 *
 *  La foto se ve ENTERA y se puede acercar (24/9/2026: «que permita
 *  hacer zoom para acercarnos y ver mejor la cara»): pellizcando, con la
 *  rueda o con − / +; arrastrando se mueve por ella. El doble clic aquí NO
 *  acerca: cada clic es una marca, y se pone al momento (revisión 24/9/2026).
 *  Las marcas van pegadas a la foto y siguen en su sitio al acercar. */
function PhotoAnnotator({ foto, pid, src, onSaved }) {
  const [markers, setMarkers] = useState(foto.markers || []);
  const [color, setColor] = useState('rojo');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setMarkers(foto.markers || []); setDirty(false); }, [foto.id]);

  // Un toque o clic sin arrastrar (x, y en % de la foto), acercada o no.
  const addMarker = ({ x, y }) => {
    setMarkers((p) => [...p, { id: mkUid(), x, y, color, note: '' }]);
    setDirty(true);
  };
  const setNote = (id, note) => { setMarkers((p) => p.map((m) => (m.id === id ? { ...m, note } : m))); setDirty(true); };
  const removeMarker = (id) => { setMarkers((p) => p.filter((m) => m.id !== id)); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/api/patients/${pid}/fotos/${foto.id}`, { markers });
      toast.success('Marcas guardadas');
      setDirty(false);
      onSaved?.(foto.id, markers);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudieron guardar las marcas');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_270px]">
      <div className="relative bg-black flex items-center justify-center min-h-[240px]">
        {/* La foto se ve ENTERA: se limita también el ancho (antes solo el
            alto, y las fotos apaisadas salían cortadas). */}
        <FotoZoom src={src} alt="" onToque={addMarker} testId="foto-visor">
          {(escala) => markers.map((m, i) => (
            <span
              key={m.id}
              className="absolute w-5 h-5 rounded-full border-2 border-white inline-flex items-center justify-center text-[10px] font-bold text-white shadow pointer-events-none"
              // Del mismo tamaño en pantalla aunque la foto esté acercada.
              style={{
                left: `${m.x}%`,
                top: `${m.y}%`,
                transform: `translate(-50%, -50%) scale(${1 / escala})`,
                backgroundColor: MK_COLORS[m.color] || MK_COLORS.rojo,
              }}
              data-testid="foto-marca"
            >
              {i + 1}
            </span>
          ))}
        </FotoZoom>
      </div>
      <div className="bg-white p-3 flex flex-col gap-2 max-h-[72vh] overflow-y-auto">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">Punto:</span>
          {['rojo', 'amarillo'].map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${color === c ? 'border-[#13231E] bg-[rgba(0,0,0,0.04)]' : 'border-[#D9E3DD]'}`}
            >
              <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: MK_COLORS[c] }} />
              {c === 'rojo' ? 'Rojo' : 'Amarillo'}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground">
          Haz clic en la foto para marcar un punto. Para verla de cerca: pellizca, usa la rueda o los botones + / −.
        </p>
        <ol className="space-y-1.5 flex-1">
          {markers.length === 0 ? (
            <li className="text-xs text-muted-foreground">Sin puntos todavía.</li>
          ) : markers.map((m, i) => (
            <li key={m.id} className="flex items-center gap-2">
              <span className="w-5 h-5 shrink-0 rounded-full inline-flex items-center justify-center text-[10px] font-bold text-white" style={{ backgroundColor: MK_COLORS[m.color] }}>{i + 1}</span>
              <input
                value={m.note}
                onChange={(e) => setNote(m.id, e.target.value)}
                placeholder="Nota (ej.: relleno, grasa…)"
                className="flex-1 min-w-0 h-8 rounded-md border border-[#dbe3ee] px-2 text-xs"
              />
              <button type="button" onClick={() => removeMarker(m.id)} className="shrink-0 text-muted-foreground hover:text-red-700" title="Quitar punto">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ol>
        <Button size="sm" onClick={save} disabled={saving || !dirty} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="foto-marcas-guardar">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />} Guardar marcas
        </Button>
      </div>
    </div>
  );
}

/** Hacer una foto en el momento con la cámara del dispositivo (móvil, tablet
 *  u ordenador) y subirla directamente a la ficha, sin guardarla antes.
 *  Vista en vivo (getUserMedia) → capturar → revisar → subir. Si el navegador
 *  no da acceso a la cámara, en móvil se ofrece la cámara nativa (input con
 *  `capture`). */
// Exposición fija (2026-08-30): −0,7 EV, el ajuste habitual de la cámara del
// móvil para fotos clínicas de cara. Si la cámara lo soporta por el navegador se aplica de verdad; si
// no (Safari/iPhone no suele exponerlo), se aplica el equivalente a la imagen
// (−0,7 EV ≈ ×0,8 de brillo en el espacio de color de la pantalla).
const EV_OBJETIVO = -0.7;
const EV_FACTOR_BRILLO = 0.8;

function CamaraDialog({ open, onClose, onSubir, tipoLabel }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const capturaInputRef = useRef(null);
  const [error, setError] = useState('');
  const [frontal, setFrontal] = useState(false); // por defecto, trasera
  const [varias, setVarias] = useState(false);   // ¿hay más de una cámara?
  const [captura, setCaptura] = useState(null);  // { blob, url } pendiente de subir
  const [subiendo, setSubiendo] = useState(false);
  // Ajustes de cámara (2026-08-30): zoom x1 (base) / x2 y EV −0,7.
  const [zoom, setZoom] = useState(1);
  const zoomRef = useRef(1);
  const [zoomNativo, setZoomNativo] = useState(false); // lo aplica la cámara (si no: recorte digital)
  const [ev, setEv] = useState(true);                  // EV −0,7 activada (por defecto sí)
  const [evNativa, setEvNativa] = useState(false);     // la aplica la cámara (si no: sobre la imagen)

  const pararStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  // Intenta aplicar el zoom EN LA CÁMARA; si el navegador no lo expone,
  // devolvemos false y se hace digital (escala en la vista + recorte al capturar).
  const aplicarZoom = async (z) => {
    setZoom(z);
    zoomRef.current = z;
    const track = streamRef.current?.getVideoTracks?.()[0];
    const caps = track?.getCapabilities?.() || {};
    if (track && caps.zoom && typeof caps.zoom.min === 'number') {
      try {
        const objetivo = Math.min(Math.max(z, caps.zoom.min), caps.zoom.max || z);
        await track.applyConstraints({ advanced: [{ zoom: objetivo }] });
        setZoomNativo(true);
        return;
      } catch { /* cae al digital */ }
    }
    setZoomNativo(false);
  };

  useEffect(() => {
    if (!open) return undefined;
    let cancelado = false;
    setError('');
    (async () => {
      pararStream();
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: frontal ? 'user' : 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        if (cancelado) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        // Exposición −0,7 nativa si la cámara la expone al navegador.
        setEvNativa(false);
        try {
          const track = stream.getVideoTracks()[0];
          const caps = track.getCapabilities?.() || {};
          if (caps.exposureCompensation && typeof caps.exposureCompensation.min === 'number') {
            const { min, max, step } = caps.exposureCompensation;
            let objetivo = Math.min(Math.max(EV_OBJETIVO, min), max);
            if (step) objetivo = Math.round(objetivo / step) * step;
            await track.applyConstraints({ advanced: [{ exposureCompensation: objetivo }] });
            if (!cancelado) setEvNativa(true);
          }
        } catch { /* sin soporte: se aplica sobre la imagen */ }
        // Reaplicar el zoom elegido al (re)abrir el stream.
        aplicarZoom(zoomRef.current);
        navigator.mediaDevices.enumerateDevices?.()
          .then((ds) => !cancelado && setVarias(ds.filter((d) => d.kind === 'videoinput').length > 1))
          .catch(() => {});
      } catch (e) {
        if (!cancelado) {
          setError(e?.name === 'NotAllowedError'
            ? 'El navegador no tiene permiso para usar la cámara. Permítelo en el candado de la barra de direcciones.'
            : 'No se pudo acceder a la cámara de este dispositivo.');
        }
      }
    })();
    return () => { cancelado = true; pararStream(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, frontal]);

  // Al capturar se desmonta el <video> y al volver a la vista en vivo se
  // monta uno nuevo sin stream: quedaba en negro (revisión 2026-09-02).
  useEffect(() => {
    if (!captura && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [captura]);

  const cerrar = () => {
    pararStream();
    if (captura?.url) URL.revokeObjectURL(captura.url);
    setCaptura(null);
    setError('');
    onClose();
  };

  const hacerFoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) { toast.error('La cámara aún no está lista'); return; }
    // Zoom digital: si la cámara no aplicó el zoom, se captura el recorte
    // central (x2 = la mitad del encuadre) a resolución real del sensor.
    const escala = !zoomNativo && zoom > 1 ? zoom : 1;
    const sw = Math.round(video.videoWidth / escala);
    const sh = Math.round(video.videoHeight / escala);
    const sx = Math.round((video.videoWidth - sw) / 2);
    const sy = Math.round((video.videoHeight - sh) / 2);
    const canvas = document.createElement('canvas');
    canvas.width = sw;
    canvas.height = sh;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, sw, sh);
    // Exposición −0,7 sobre la imagen cuando la cámara no la aplica ella
    // (multiplicación por píxel: canvas.filter no existe en Safari/iPhone).
    if (ev && !evNativa) {
      try {
        const img = ctx.getImageData(0, 0, sw, sh);
        const d = img.data;
        for (let i = 0; i < d.length; i += 4) {
          d[i] *= EV_FACTOR_BRILLO;
          d[i + 1] *= EV_FACTOR_BRILLO;
          d[i + 2] *= EV_FACTOR_BRILLO;
        }
        ctx.putImageData(img, 0, 0);
      } catch { /* si el canvas está "tainted" (no debería), sale sin ajuste */ }
    }
    canvas.toBlob((blob) => {
      if (!blob) { toast.error('No se pudo capturar la foto'); return; }
      setCaptura({ blob, url: URL.createObjectURL(blob) });
    }, 'image/jpeg', 0.92);
  };

  const repetir = () => {
    if (captura?.url) URL.revokeObjectURL(captura.url);
    setCaptura(null);
  };

  const subir = async () => {
    if (!captura) return;
    setSubiendo(true);
    const nombre = `captura_${new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-')}.jpg`;
    const file = new File([captura.blob], nombre, { type: 'image/jpeg' });
    const ok = await onSubir(file);
    setSubiendo(false);
    if (ok) repetir(); // vuelve a la vista en vivo para poder hacer otra
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && cerrar()}>
      <DialogContent className="max-w-2xl p-0 bg-white border-none overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 pr-12 border-b border-border">
          <DialogTitle className="text-sm font-medium text-[#13231E] inline-flex items-center gap-2">
            <Camera className="w-4 h-4 text-[#2E6E5E]" /> Hacer foto
            <span className="text-muted-foreground font-normal">· se guardará como <strong className="text-[#2E6E5E]">{tipoLabel}</strong></span>
          </DialogTitle>
        </div>

        <div className="relative bg-black flex items-center justify-center min-h-[300px] overflow-hidden">
          {error ? (
            <div className="text-center text-white/90 text-sm px-6 py-10 max-w-sm">
              <Camera className="w-8 h-8 mx-auto mb-3 opacity-60" />
              <p>{error}</p>
              {esMovil() && (
                <>
                  {/* Plan B en móvil: la cámara nativa del sistema */}
                  <input
                    ref={capturaInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="sr-only"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (f) { const ok = await onSubir(f); if (ok) cerrar(); }
                    }}
                  />
                  <Button size="sm" className="mt-4 bg-[#2E6E5E] hover:bg-[#1E4A3F]" onClick={() => capturaInputRef.current?.click()}>
                    <Camera className="w-4 h-4 mr-1.5" /> Abrir la cámara del móvil
                  </Button>
                </>
              )}
            </div>
          ) : captura ? (
            <img src={captura.url} alt="Captura" className="max-h-[65vh] max-w-full object-contain" />
          ) : (
            /* La cámara frontal se ve en espejo (como un espejo real); la foto
               capturada se guarda sin invertir. El zoom digital y la EV −0,7
               se enseñan en la vista tal y como quedarán en la foto. */
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="max-h-[65vh] max-w-full"
              style={{
                transform: [
                  frontal ? 'scaleX(-1)' : '',
                  !zoomNativo && zoom > 1 ? `scale(${zoom})` : '',
                ].filter(Boolean).join(' ') || undefined,
                filter: ev && !evNativa ? `brightness(${EV_FACTOR_BRILLO})` : undefined,
              }}
              data-testid="camara-video"
            />
          )}
        </div>

        <div className="flex items-center justify-center gap-3 px-4 py-3 bg-white">
          {captura ? (
            <>
              <Button variant="outline" size="sm" onClick={repetir} disabled={subiendo} data-testid="camara-repetir">
                <RefreshCcw className="w-4 h-4 mr-1.5" /> Repetir
              </Button>
              <Button size="sm" onClick={subir} disabled={subiendo} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="camara-subir">
                {subiendo ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
                {subiendo ? 'Subiendo…' : 'Subir a la ficha'}
              </Button>
            </>
          ) : (
            <>
              {!error && (
                <div className="inline-flex rounded-full border border-[#D9E3DD] overflow-hidden" data-testid="camara-zoom">
                  {[1, 2].map((z) => (
                    <button
                      key={z}
                      type="button"
                      onClick={() => aplicarZoom(z)}
                      className={`px-3 py-1 text-xs font-semibold ${zoom === z ? 'bg-[#2E6E5E] text-white' : 'text-[#13231E] hover:bg-[rgba(46,110,94,0.08)]'}`}
                      data-testid={`camara-zoom-${z}x`}
                    >
                      {z}x
                    </button>
                  ))}
                </div>
              )}
              {!error && (
                <button
                  type="button"
                  onClick={() => setEv((v) => !v)}
                  className={`px-3 py-1 rounded-full border text-xs font-semibold ${ev ? 'bg-[#2E6E5E] text-white border-[#2E6E5E]' : 'text-[#13231E] border-[#D9E3DD] hover:bg-[rgba(46,110,94,0.08)]'}`}
                  title={ev ? 'Exposición −0,7 activada (toca para quitarla)' : 'Exposición normal (toca para poner −0,7)'}
                  data-testid="camara-ev"
                >
                  EV −0,7{ev ? ' ✓' : ''}
                </button>
              )}
              {varias && !error && (
                <Button variant="outline" size="sm" onClick={() => setFrontal((f) => !f)} title="Cambiar de cámara" data-testid="camara-cambiar">
                  <RefreshCcw className="w-4 h-4 mr-1.5" /> Cambiar cámara
                </Button>
              )}
              {!error && (
                <Button size="sm" onClick={hacerFoto} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="camara-capturar">
                  <Camera className="w-4 h-4 mr-1.5" /> Capturar
                </Button>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Galería de fotos clínicas del paciente (antes / después).
 * Subir desde el móvil (cámara o carrete) o el ordenador; ver a pantalla
 * completa; borrar. Los bytes se sirven autenticados desde el backend.
 */
export default function PatientPhotosTab({ patient }) {
  const pid = patient?.id;
  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const [tipoSel, setTipoSel] = useState('antes');
  const [lightbox, setLightbox] = useState(null); // foto ampliada
  const [dragOver, setDragOver] = useState(false); // arrastrar y soltar
  const [camaraOpen, setCamaraOpen] = useState(false); // hacer foto en el momento
  const inputRef = useRef(null);
  const inputId = `foto-input-${pid || 'x'}`;

  const cargar = async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/patients/${pid}/fotos`);
      setFotos(res.data || []);
    } catch {
      toast.error('No se pudieron cargar las fotos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [pid]);

  const onFiles = async (files) => {
    if (!files || files.length === 0) return;
    if (!pid) { toast.error('No se pudo identificar al paciente; recarga la ficha.'); return; }
    setSubiendo(true);
    let ok = 0;
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tipo', tipoSel);
      try {
        await api.post(`/api/patients/${pid}/fotos`, fd);
        ok += 1;
      } catch (e) {
        toast.error(e?.response?.data?.detail || `No se pudo subir ${file.name}`);
      }
    }
    setSubiendo(false);
    if (inputRef.current) inputRef.current.value = '';
    if (ok > 0) {
      toast.success(ok === 1 ? 'Foto subida' : `${ok} fotos subidas`);
      cargar();
    }
  };

  // Subida de UNA foto hecha con la cámara; devuelve si fue bien (el diálogo
  // vuelve a la vista en vivo para poder hacer otra).
  const subirCaptura = async (file) => {
    if (!pid) { toast.error('No se pudo identificar al paciente; recarga la ficha.'); return false; }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('tipo', tipoSel);
    try {
      await api.post(`/api/patients/${pid}/fotos`, fd);
      toast.success('Foto subida a la ficha');
      cargar();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo subir la foto');
      return false;
    }
  };

  const borrar = async (foto) => {
    if (!window.confirm('¿Borrar esta foto? No se puede deshacer.')) return;
    try {
      await api.delete(`/api/patients/${pid}/fotos/${foto.id}`);
      setFotos((prev) => prev.filter((f) => f.id !== foto.id));
      if (lightbox?.id === foto.id) setLightbox(null);
      toast.success('Foto borrada');
    } catch {
      toast.error('No se pudo borrar la foto');
    }
  };

  // La foto ENTERA (visor y descarga) o su miniatura, también de la foto
  // entera, para la galería (24/9/2026: las fotos se guardan a su tamaño y
  // no hace falta bajarse 5 MB para pintar un cuadrito).
  const imgUrl = (foto, tam) => `${BACKEND_URL}/api/patients/${pid}/fotos/${foto.id}/img${tam ? `?tam=${tam}` : ''}`;

  // Nombre de archivo al descargar: paciente_grupo_fecha.ext
  const fotoFileName = (foto) => {
    const mime = foto.mime || '';
    const ext = mime.includes('png') ? 'png'
      : mime.includes('webp') ? 'webp'
        : mime.includes('heic') || mime.includes('heif') ? 'heic'
          : mime.includes('gif') ? 'gif' : 'jpg';
    const nombre = [patient?.name, patient?.surname1].filter(Boolean).join(' ') || 'paciente';
    const grupo = GRUPOS.find((g) => g.tipo === (foto.tipo || 'otra'))?.label || 'foto';
    return `${nombre}_${grupo}_${foto.taken_date || ''}.${ext}`.replace(/[^\w.-]+/g, '_');
  };

  // Tras guardar marcas en una foto: refleja los puntos en la galería y el visor.
  const onFotoSaved = (fotoId, markers) => {
    setFotos((prev) => prev.map((f) => (f.id === fotoId ? { ...f, markers } : f)));
    setLightbox((lb) => (lb && lb.id === fotoId ? { ...lb, markers } : lb));
  };

  return (
    <SectionCard
      title={<span className="inline-flex items-center gap-2"><Images className="w-4 h-4 text-[#13231E]" /> Fotos del paciente</span>}
    >
      {/* Barra de subida */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-sm text-muted-foreground">Subir como:</span>
        {GRUPOS.map((g) => (
          <button
            key={g.tipo}
            type="button"
            onClick={() => setTipoSel(g.tipo)}
            className={`text-sm px-3 py-1 rounded-full border transition ${
              tipoSel === g.tipo
                ? 'bg-[#2E6E5E] text-white border-[#2E6E5E]'
                : 'bg-transparent text-[#13231E] border-border hover:bg-[rgba(46,110,94,0.06)]'
            }`}
          >
            {g.label}
          </button>
        ))}
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setCamaraOpen(true)}
          className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium border border-[#2E6E5E] text-[#2E6E5E] hover:bg-[rgba(46,110,94,0.08)] transition-colors"
          data-testid="foto-camara-abrir"
        >
          <Camera className="w-4 h-4 mr-2" /> Hacer foto
        </button>
        {/* Input + <label>: el selector se abre de forma NATIVA (sin JS), que es
            mucho más fiable en móvil y navegadores con restricciones. */}
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept="image/*"
          multiple
          className="sr-only"
          onChange={(e) => onFiles(e.target.files)}
          data-testid="foto-input"
        />
        <label
          htmlFor={inputId}
          className={`inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-white cursor-pointer transition-colors bg-[#2E6E5E] hover:bg-[#1E4A3F] ${
            subiendo ? 'opacity-60 pointer-events-none' : ''
          }`}
          data-testid="foto-subir"
        >
          {subiendo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
          {subiendo ? 'Subiendo…' : 'Subir fotos'}
        </label>
      </div>

      {/* Arrastrar y soltar ficheros para subirlos (2026-07-21) */}
      <div
        onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); onFiles(e.dataTransfer?.files); }}
        className={`mb-4 rounded-lg border-2 border-dashed px-4 py-5 text-center text-sm transition-colors ${
          dragOver
            ? 'border-[#2E6E5E] bg-[rgba(46,110,94,0.10)] text-[#13231E]'
            : 'border-border text-muted-foreground'
        }`}
        data-testid="foto-dropzone"
      >
        <Upload className="w-5 h-5 mx-auto mb-1 text-[#2E6E5E]" />
        Arrastra aquí las fotos para subirlas, o pulsa «Subir fotos».
        <div className="text-xs mt-1">
          Se guardarán como <strong className="text-[#2E6E5E]">{GRUPOS.find((g) => g.tipo === tipoSel)?.label}</strong>
          {' '}· desde el móvil puedes hacer la foto en el momento · máx. 25 MB por foto.
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando fotos…
        </div>
      ) : fotos.length === 0 ? (
        <div className="text-center text-muted-foreground py-10">
          <Camera className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">Aún no hay fotos de este paciente.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {GRUPOS.map((g) => {
            const items = fotos.filter((f) => (f.tipo || 'otra') === g.tipo);
            if (items.length === 0) return null;
            return (
              <div key={g.tipo}>
                <div className="text-sm font-semibold text-[#13231E] mb-2">
                  {g.label} <span className="text-muted-foreground font-normal">({items.length})</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {items.map((foto) => (
                    <figure key={foto.id} className="group relative rounded-lg overflow-hidden border border-border bg-[rgba(46,110,94,0.03)]">
                      {/* La foto ENTERA en el cuadrito (contain, no cover: que
                          no se corte la cabeza en la miniatura). */}
                      <img
                        src={imgUrl(foto, 'mini')}
                        alt={foto.caption || g.label}
                        loading="lazy"
                        className="w-full h-36 object-contain bg-[#EEF2F0] cursor-zoom-in"
                        onClick={() => setLightbox(foto)}
                        data-testid="foto-miniatura"
                      />
                      {foto.markers?.length > 0 && (
                        <span className="absolute top-1 left-1 inline-flex items-center rounded-full bg-[#2E6E5E] text-white text-[10px] font-bold px-1.5 py-0.5 shadow" title={`${foto.markers.length} punto(s) marcado(s)`}>
                          ● {foto.markers.length}
                        </span>
                      )}
                      <figcaption className="flex items-center justify-between px-2 py-1 text-[11px] text-muted-foreground">
                        <span>{foto.taken_date ? formatDate(foto.taken_date) : ''}</span>
                        <button
                          type="button"
                          onClick={() => borrar(foto)}
                          className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition text-destructive p-1 -mr-1"
                          title="Borrar foto"
                          data-testid="foto-borrar"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Hacer foto con la cámara del dispositivo y subirla directamente. */}
      <CamaraDialog
        open={camaraOpen}
        onClose={() => setCamaraOpen(false)}
        onSubir={subirCaptura}
        tipoLabel={GRUPOS.find((g) => g.tipo === tipoSel)?.label || 'Otras'}
      />

      {/* Visor a pantalla completa con marcadores (puntos rojo/amarillo + nota). */}
      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-5xl p-0 bg-white border-none overflow-hidden">
          {lightbox && (
            <div>
              <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-white">
                <span className="text-sm text-[#13231E]">
                  {GRUPOS.find((g) => g.tipo === (lightbox.tipo || 'otra'))?.label}
                  {lightbox.taken_date ? ` · ${formatDate(lightbox.taken_date)}` : ''}
                  {lightbox.uploaded_by_name ? ` · ${lightbox.uploaded_by_name}` : ''}
                </span>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      // En móvil, pestaña nueva: el enlace con `download`
                      // bloqueaba el atrás en iOS (2026-07-25).
                      if (esMovil()) {
                        window.open(imgUrl(lightbox), '_blank', 'noopener');
                        return;
                      }
                      const a = document.createElement('a');
                      a.href = imgUrl(lightbox);
                      a.download = fotoFileName(lightbox);
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                    }}
                    className="inline-flex items-center gap-1 text-[#2E6E5E] hover:text-[#1E4A3F] text-sm font-medium"
                    data-testid="foto-descargar"
                  >
                    <Download className="w-4 h-4" /> Descargar
                  </button>
                  <button type="button" onClick={() => borrar(lightbox)} className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 text-sm">
                    <Trash2 className="w-4 h-4" /> Borrar
                  </button>
                  <button type="button" onClick={() => setLightbox(null)} className="text-muted-foreground hover:text-[#13231E]" aria-label="Cerrar">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <PhotoAnnotator foto={lightbox} pid={pid} src={imgUrl(lightbox)} onSaved={onFotoSaved} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}
